import React from 'react';
import { Card, Space, Tag, Row, Col, Button, Divider, Form, Input, InputNumber, DatePicker, Select, Badge, Typography, Descriptions, Modal } from 'antd';
import {
    UserOutlined,
    PlusOutlined,
    LinkOutlined,
    PhoneOutlined,
    EnvironmentOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import DebounceSelect from './DebounceSelect';
import { useCallCenter } from '../CallCenterContext';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const ParticipantPanel = () => {
    const {
        status,
        isInCall,
        isSelectorModalVisible,
        participantCandidates,
        handleSelectParticipant,
        handleCloseSelector,
        handleStartAssociatingParticipant,
        handleStartCreatingParticipant,
        showAddParticipant,
        isCreatingParticipant,
        isAssociatingParticipant,
        addParticipantForm,
        handleCancelCreatingParticipant,
        handleSaveParticipant,
        availableGroups,
        fetchUbigeo,
        associationForm,
        fetchParticipants,
        handleCancelAssociatingParticipant,
        handleAssociateParticipant,
        isSavingParticipant,
        currentContact,
        currentCall,
        campaigns,
        hasOutbound,
        handleOpenAddCardModal,
        isAddCardModalVisible,
        handleSaveCard,
        handleCloseAddCardModal,
        addCardForm,
        availableTags
    } = useCallCenter();

    // Estado derivado
    const hasInbound = React.useMemo(() => campaigns.some(c => c.type === 'inbound'), [campaigns]);

    // 1. SELECCIÓN DE PARTICIPANTE (AMBIGÜEDAD)
    if (isSelectorModalVisible) {
        return (
            <Card
                className="participant-selector-card"
                title={<Space><UserOutlined /> Identificación de Llamada</Space>}
                extra={<Tag color="volcano">AMBIGUO</Tag>}
                style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    background: '#fff2f0',
                    border: '1px solid #ffccc7'
                }}
                styles={{ body: { flex: 1, overflowY: 'auto' } }}
            >
                <div style={{ marginBottom: 16, textAlign: 'center' }}>
                    <Text strong style={{ display: 'block', marginBottom: 5 }}>
                        {status === 'ringing' ? 'Llamada Entrante: Múltiples Coincidencias' : 'Número asociado a múltiples registros'}
                    </Text>
                    <Text type="secondary">
                        {status === 'ringing'
                            ? 'Información disponible. Responda para seleccionar.'
                            : (status === 'available' ? 'Realice la llamada para confirmar el participante.' : 'Por favor, pregunte el nombre y seleccione:')}
                    </Text>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {participantCandidates && participantCandidates.length > 0 ? (
                        participantCandidates.map(p => (
                            <Card
                                key={p.id}
                                hoverable={status === 'incall' || status === 'wrapup'}
                                size="small"
                                style={{ cursor: (status === 'incall' || status === 'wrapup') ? 'pointer' : 'default', border: '1px solid #d9d9d9' }}
                                onClick={() => (status === 'incall' || status === 'wrapup') && handleSelectParticipant(p.id)}
                                styles={{ body: { padding: '10px' } }}
                            >
                                <Row align="middle" justify="space-between">
                                    <Col flex="auto">
                                        <div style={{ fontWeight: 'bold', fontSize: 14 }}>{p.name || 'Sin Nombre'}</div>
                                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>{p.document_type}: {p.document_number}</div>
                                    </Col>
                                    <Col>
                                        {(status === 'incall' || status === 'wrapup') && (
                                            <Button
                                                type="primary"
                                                size="small"
                                                ghost
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSelectParticipant(p.id);
                                                }}
                                            >
                                                Elegir
                                            </Button>
                                        )}
                                    </Col>
                                </Row>
                            </Card>
                        ))
                    ) : (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                            No se cargaron los candidatos correctamente.
                        </div>
                    )}


                    {(status === 'incall' || status === 'wrapup') && (
                        <>
                            <Divider plain>¿Ninguno coincide?</Divider>
                            <Button block onClick={handleCloseSelector} style={{ marginBottom: 8 }}>
                                Crear Nuevo Participante
                            </Button>
                            <Button
                                block
                                onClick={() => {
                                    handleCloseSelector(); // Verificación lógica: originalmente handleCloseSelector establece visible falso O manejador dedicado
                                    // En original: setIsSelectorModalVisible(false); handleStartAssociatingParticipant();
                                    handleStartAssociatingParticipant();
                                }}
                            >
                                Asociar a otro participante
                            </Button>
                        </>
                    )}
                </div>
            </Card>
        );
    }

    // 2. CREACIÓN / ASOCIACIÓN (PARTICIPANTE DESCONOCIDO)
    // Mostrar si se solicitó explícitamente O si tenemos un contacto activo que no es un participante registrado (sin UUID y sin participante anidado)
    // [FIX] Check for nested participant
    const isProspect = currentContact && !currentContact.participant && !currentContact.uuid;

    // [FIX] Define effective participant for display (Nested takes precedence)
    // If manual contact (name is 'Contacto Manual'), we force 'No Info' regardless of participant structure
    const isManualWithoutParticipant = currentContact?.name === 'Contacto Manual';
    const displayParticipant = isManualWithoutParticipant ? null : (currentContact?.participant || currentContact);

    if (showAddParticipant || isProspect) {
        return (
            <Card
                className="add-participant-card"
                id="add-participant-card"
                style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed #d9d9d9',
                    background: '#fafafa',
                    overflowY: 'auto'
                }}
            >
                {!isCreatingParticipant && !isAssociatingParticipant ? (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            background: '#f5f5f5',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 16
                        }}>
                            <UserOutlined style={{ fontSize: 40, color: '#bfbfbf' }} />
                        </div>
                        <Title level={4} style={{ color: '#595959', marginBottom: 8 }}>Participante No Identificado</Title>
                        <Paragraph type="secondary" style={{ marginBottom: 24 }}>
                            El número no está registrado en la base de participantes.
                        </Paragraph>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            {(status === 'incall' || status === 'wrapup') && (
                                <>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        size="large"
                                        onClick={handleStartCreatingParticipant}
                                        block
                                    >
                                        Crear Nuevo Participante
                                    </Button>
                                    <Button
                                        icon={<LinkOutlined />}
                                        size="large"
                                        onClick={handleStartAssociatingParticipant}
                                        block
                                    >
                                        Asociar a existente
                                    </Button>
                                </>
                            )}
                        </Space>
                    </div>
                ) : isAssociatingParticipant ? (
                    <div style={{ width: '100%', padding: '0 20px', textAlign: 'left' }}>
                        <Title level={5} style={{ marginBottom: 15, textAlign: 'center' }}>Asociar Participante</Title>
                        <Form
                            form={associationForm}
                            layout="vertical"
                            size="small"
                        >
                            <Form.Item
                                name="participant"
                                label="Buscar Participante (Nombre o DNI)"
                                rules={[{ required: true, message: 'Seleccione un participante' }]}
                            >
                                <DebounceSelect
                                    showSearch
                                    placeholder="Escriba para buscar..."
                                    fetchOptions={fetchParticipants}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                            <Space style={{ width: '100%', justifyContent: 'center' }}>
                                <Button onClick={handleCancelAssociatingParticipant} size="small">Cancelar</Button>
                                <Button type="primary" onClick={handleAssociateParticipant} size="small" loading={isSavingParticipant}>Asociar</Button>
                            </Space>
                        </Form>
                    </div>
                ) : (
                    <div style={{ width: '100%', padding: '0 20px', textAlign: 'left' }}>
                        <Title level={5} style={{ marginBottom: 15, textAlign: 'center' }}>Nuevo Registro</Title>
                        <Form
                            form={addParticipantForm}
                            layout="vertical"
                            name="add_participant_form_card"
                            size="small"
                        >
                            <Row gutter={8}>
                                <Col span={24}>
                                    <Form.Item
                                        name="fullName"
                                        label="Nombres Completos"
                                        rules={[{ required: true, message: 'Requerido' }]}
                                        style={{ marginBottom: 8 }}
                                    >
                                        <Input placeholder="Ej. Juan Pérez" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="homecode"
                                        label="Código hogar"
                                        style={{ marginBottom: 8 }}
                                    >
                                        <InputNumber style={{ width: '100%' }} maxLength={10} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="registryDate"
                                        label="Fecha de registro"
                                        initialValue={dayjs()}
                                        rules={[{ required: true, message: 'Requerido' }]}
                                        style={{ marginBottom: 8 }}
                                    >
                                        <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="gender"
                                        label="Género"
                                        style={{ marginBottom: 8 }}
                                    >
                                        <Select placeholder="Seleccionar" allowClear>
                                            <Option value="M">Masculino</Option>
                                            <Option value="F">Femenino</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="age"
                                        label="Edad"
                                        style={{ marginBottom: 8 }}
                                    >
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="documentType"
                                        label="Tipo Doc."
                                        rules={[{ required: true, message: 'Requerido' }]}
                                        style={{ marginBottom: 8 }}
                                    >
                                        <Select>
                                            <Option value="DNI">DNI</Option>
                                            <Option value="CE">CE</Option>
                                            <Option value="PA">Pasaporte</Option>
                                            <Option value="RUC">RUC</Option>
                                            <Option value="CPP">CPP</Option>
                                            <Option value="Otro">Otro</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="documentNumber"
                                        label="Nro. Doc."
                                        rules={[{ required: true, message: 'Requerido' }]}
                                        style={{ marginBottom: 8 }}
                                    >
                                        <Input placeholder="12345678" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="phone"
                                        label="Teléfono"
                                        rules={[{ required: true, message: 'Requerido' }]}
                                        style={{ marginBottom: 8 }}
                                    >
                                        <Input placeholder="987654321" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="email"
                                        label="Email"
                                        style={{ marginBottom: 8 }}
                                    >
                                        <Input placeholder="juan@mail.com" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="nationality"
                                        label="Nacionalidad"
                                        style={{ marginBottom: 8 }}
                                    >
                                        <Input placeholder="Ej. Peruana" />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item
                                        name="cardNumber"
                                        label="Tarjeta"
                                        style={{ marginBottom: 8 }}
                                    >
                                        <Input placeholder="Número de tarjeta" />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item
                                        name="address"
                                        label="Dirección"
                                        style={{ marginBottom: 8 }}
                                    >
                                        <Input placeholder="Av. Principal 123" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="ubigeo"
                                        label="Ubigeo (Distrito)"
                                        rules={[{ required: true, message: 'Requerido' }]}
                                        style={{ marginBottom: 8 }}
                                    >
                                        <DebounceSelect
                                            showSearch
                                            placeholder="Buscar distrito..."
                                            fetchOptions={fetchUbigeo}
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item
                                        name="groups"
                                        label="Grupos"
                                        style={{ marginBottom: 12 }}
                                    >
                                        <Select
                                            mode="multiple"
                                            placeholder="Seleccionar grupos..."
                                            optionFilterProp="children"
                                        >
                                            {availableGroups.map(g => (
                                                <Option key={g.id} value={g.id}>{g.name}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Space style={{ width: '100%', justifyContent: 'center' }}>
                                <Button onClick={handleCancelCreatingParticipant} size="small">Cancelar</Button>
                                <Button type="primary" onClick={handleSaveParticipant} size="small">Guardar</Button>
                            </Space>
                        </Form>
                    </div>
                )}
            </Card>
        );
    }

    // 3. INFORMACIÓN DEL PARTICIPANTE (ACTUAL)
    return (
        <>
            <Card
                className="participant-info-card"
                id="participant-info-card"
                title={<Space><UserOutlined /> Información del Participante</Space>}
                extra={
                    <Tag color="green">ACTIVO</Tag>
                }
                styles={{ body: { padding: '16px' } }}
            >
                {/* Contenido del participante (avatar, descripciones, tarjetas detalladas, tags) */}
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    {displayParticipant && (
                        <Badge count={<PhoneOutlined style={{ color: '#52c41a' }} />} offset={[-5, 35]}>
                            <div style={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: '#f0f2f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto',
                                border: '2px solid #E60000'
                            }}>
                                <UserOutlined style={{ fontSize: 40, color: '#E60000' }} />
                            </div>
                        </Badge>
                    )}
                    <Title level={4} style={{ marginTop: 15, marginBottom: 0, padding: '0 10px', lineHeight: '1.2' }}>
                        {displayParticipant ? (
                            <Space direction="vertical" size={0}>
                                {displayParticipant.homecode && <Text type="secondary" style={{ fontSize: '14px', display: 'block' }}>ID: {displayParticipant.homecode}</Text>}
                                <Text>{displayParticipant.name}</Text>
                            </Space>
                        ) : (
                            <Text type="secondary" style={{ fontSize: '16px' }}>
                                {isInCall ? 'Sin información' : 'Sin llamada activa'}
                            </Text>
                        )}
                    </Title>
                </div>

                <Divider style={{ margin: '12px 0' }} />

                {displayParticipant && (
                    <>
                        <Descriptions column={1} size="small" bordered>
                            {/* Metadatos de campaña (Dinámico) - SOLO si es contacto raiz, no participante anidado (Opcional, pero usuario pidió separar) */}
                            {/* Si es participante CRM, NO mostramos metadata de campaña AQUÍ duplicada, la mostramos en el Widget izquierdo */}
                            {displayParticipant.metadata && !displayParticipant.uuid && (
                                Object.entries(typeof displayParticipant.metadata === 'string' ? JSON.parse(displayParticipant.metadata || '{}') : displayParticipant.metadata).map(([key, value]) => (
                                    <Descriptions.Item key={key} label={key.replace(/_/g, ' ').toUpperCase()}>
                                        <Text strong style={{ color: '#1890ff' }}>{value}</Text>
                                    </Descriptions.Item>
                                ))
                            )}
                            <Descriptions.Item label="Código hogar">

                                <Text strong>{displayParticipant.homecode || '--'}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Fecha de registro">{displayParticipant.registry_date || '--'}</Descriptions.Item>
                            <Descriptions.Item label="Género">
                                {displayParticipant.gender === 'M' ? 'Masculino' : (displayParticipant.gender === 'F' ? 'Femenino' : '--')}
                            </Descriptions.Item>
                            <Descriptions.Item label="Edad">{displayParticipant.age || '--'}</Descriptions.Item>
                            <Descriptions.Item label="Nacionalidad">{displayParticipant.nationality || '--'}</Descriptions.Item>
                            <Descriptions.Item label="Teléfono">
                                <Text copyable>
                                    {(displayParticipant.phones && displayParticipant.phones.length > 0)
                                        ? displayParticipant.phones[0]
                                        : (displayParticipant.phone || currentCall?.source || '--')}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Documento">
                                {displayParticipant.document_type} {displayParticipant.document_number}
                            </Descriptions.Item>
                            <Descriptions.Item label="Ubicación">
                                <Space direction="vertical" size={0}>
                                    <Space>
                                        <EnvironmentOutlined />
                                        {displayParticipant.address || '--'}
                                    </Space>
                                    {displayParticipant.ubigeo_text && (
                                        <Text type="secondary" style={{ fontSize: '11px', marginLeft: 22, display: 'block' }}>
                                            {displayParticipant.ubigeo_text}
                                        </Text>
                                    )}
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="Email">{displayParticipant.email || '--'}</Descriptions.Item>
                            <Descriptions.Item label="Tarjeta">{displayParticipant.card_number || '--'}</Descriptions.Item>
                            <Descriptions.Item label="Último Contacto">{displayParticipant.last_contact || 'Nunca'}</Descriptions.Item>
                        </Descriptions>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Información Detallada</Text>
                            <Button
                                type="link"
                                size="small"
                                icon={<PlusOutlined style={{ fontSize: '10px', color: '#000' }} />}
                                onClick={handleOpenAddCardModal}
                                style={{ padding: 0, height: 'auto', color: '#000', fontSize: '11px' }}
                            >
                                agregar tarjeta
                            </Button>
                        </div>
                        <Divider style={{ margin: '0 0 12px 0' }} />

                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            {displayParticipant.cards && displayParticipant.cards.length > 0 ? (
                                displayParticipant.cards.map(card => (
                                    <Card
                                        key={card.id}
                                        size="small"
                                        className="glass-card"
                                        title={<Text strong>{card.title}</Text>}
                                        styles={{
                                            header: { backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0' },
                                            body: { backgroundColor: '#ffffff', padding: '12px' }
                                        }}
                                    >
                                        <Paragraph style={{ fontSize: '13px', marginBottom: 8 }}>
                                            {card.description}
                                        </Paragraph>
                                        <div style={{ marginTop: 8 }}>
                                            {card.tags && card.tags.length > 0 ? (
                                                card.tags.map((tag, idx) => (
                                                    <Tag key={tag.id || idx} color={tag.color} style={{ fontSize: '10px', margin: '0 4px 4px 0' }}>
                                                        {tag.name}
                                                    </Tag>
                                                ))
                                            ) : null}
                                        </div>
                                    </Card>
                                ))
                            ) : (
                                <Text type="secondary" style={{ display: 'block', textAlign: 'center' }}>No hay tarjetas de información</Text>
                            )}
                        </Space>
                    </>
                )}
                {/* Botón de pie de página para nuevo participante */}
                {/* Botones de pie de página: Siempre visibles si tenemos un contacto o si es entrada sin contacto */}
                {['ringing', 'incall', 'wrapup'].includes(status) ? (
                    <div style={{ marginTop: 20, textAlign: 'center' }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Button
                                block
                                type="dashed"
                                icon={<PlusOutlined />}
                                onClick={handleStartCreatingParticipant}
                            >
                                Crear Nuevo Participante
                            </Button>
                            <Button
                                block
                                type="dashed"
                                icon={<LinkOutlined />}
                                onClick={handleStartAssociatingParticipant}
                            >
                                Asociar a existente
                            </Button>
                        </Space>
                    </div>
                ) : null}
            </Card>

            {/* Modal para Agregar Nueva Tarjeta (Card) */}
            <Modal
                title="Agregar Nueva Tarjeta de Información"
                open={isAddCardModalVisible}
                onOk={handleSaveCard}
                onCancel={handleCloseAddCardModal}
                okText="Guardar Tarjeta"
                cancelText="Cancelar"
                centered
            >
                <Form
                    form={addCardForm}
                    layout="vertical"
                    name="add_card_form"
                >
                    <Form.Item
                        name="title"
                        label="Título de la Tarjeta"
                        rules={[{ required: true, message: 'Por favor ingrese un título' }]}
                    >
                        <Input placeholder="Ej. Datos Médicos, Educación, etc." />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Contenido / Descripción"
                        rules={[{ required: true, message: 'Por favor ingrese el contenido' }]}
                    >
                        <Input.TextArea rows={4} placeholder="Detalle la información aquí..." />
                    </Form.Item>
                    <Form.Item
                        name="tags"
                        label="Etiquetas (Tags)"
                    >
                        <Select
                            mode="multiple"
                            placeholder="Seleccione etiquetas"
                            style={{ width: '100%' }}
                            options={availableTags.map(tag => ({
                                value: tag.id,
                                label: (
                                    <Space>
                                        <Badge color={tag.color} />
                                        {tag.name}
                                    </Space>
                                )
                            }))}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default ParticipantPanel;
